import { Provider } from '@angular/core';
import { injectDispatch } from '@ngrx/signals/events';
import { TransactionEvents } from '@app/domain/events';
import { IncomeDomainService } from '@app/domain/services';
import {
  GetIncomesUseCase,
  GetIncomeByIdUseCase,
  GetIncomesTotalAmountUseCase,
  CountIncomesUseCase,
  CreateIncomeUseCase,
  UpdateIncomeUseCase,
  DeleteIncomeUseCase,
  ExportIncomesUseCase,
  BulkSetIncomeCategoryUseCase,
  SplitIncomeUseCase,
} from '@app/domain/use-cases';
import type {
  IExternalTransactionRepository,
  IIncomeRepository,
  ITransactionManager,
} from '@app/domain/ports';
import {
  EXTERNAL_TRANSACTION_REPOSITORY,
  INCOME_REPOSITORY,
  TRANSACTION_MANAGER,
} from '../../tokens/infrastructure.tokens';

/**
 * Provides Income domain services and use cases.
 *
 * Includes:
 * - IncomeDomainService: Business rules and validation
 * - 7 Use Cases: GetIncomes, GetIncomeById, GetTotal, Count, Create, Update, Delete
 *
 * All providers use constructor injection pattern following Clean Architecture.
 */
export function provideIncomeDomain(): Provider[] {
  return [
    // Domain Service
    {
      provide: IncomeDomainService,
      useFactory: () => new IncomeDomainService(),
      deps: [],
    },

    // Use Cases - Query
    {
      provide: GetIncomesUseCase,
      useFactory: (repository: IIncomeRepository) => new GetIncomesUseCase(repository),
      deps: [INCOME_REPOSITORY],
    },
    {
      provide: GetIncomeByIdUseCase,
      useFactory: (repository: IIncomeRepository) => new GetIncomeByIdUseCase(repository),
      deps: [INCOME_REPOSITORY],
    },
    {
      provide: GetIncomesTotalAmountUseCase,
      useFactory: (repository: IIncomeRepository) => new GetIncomesTotalAmountUseCase(repository),
      deps: [INCOME_REPOSITORY],
    },
    {
      provide: CountIncomesUseCase,
      useFactory: (repository: IIncomeRepository) => new CountIncomesUseCase(repository),
      deps: [INCOME_REPOSITORY],
    },

    // Use Cases - Commands
    {
      provide: CreateIncomeUseCase,
      useFactory: (repository: IIncomeRepository, domainService: IncomeDomainService) => {
        const dispatch = injectDispatch(TransactionEvents);
        return new CreateIncomeUseCase(repository, domainService, dispatch);
      },
      deps: [INCOME_REPOSITORY, IncomeDomainService],
    },
    {
      provide: UpdateIncomeUseCase,
      useFactory: (repository: IIncomeRepository, domainService: IncomeDomainService) => {
        const dispatch = injectDispatch(TransactionEvents);
        return new UpdateIncomeUseCase(repository, domainService, dispatch);
      },
      deps: [INCOME_REPOSITORY, IncomeDomainService],
    },
    {
      provide: DeleteIncomeUseCase,
      useFactory: (repository: IIncomeRepository, domainService: IncomeDomainService) => {
        const dispatch = injectDispatch(TransactionEvents);
        return new DeleteIncomeUseCase(repository, domainService, dispatch);
      },
      deps: [INCOME_REPOSITORY, IncomeDomainService],
    },
    {
      provide: SplitIncomeUseCase,
      useFactory: (
        repository: IIncomeRepository,
        externalTransactionRepository: IExternalTransactionRepository,
        transactionManager: ITransactionManager,
        domainService: IncomeDomainService,
      ) => {
        const dispatch = injectDispatch(TransactionEvents);
        return new SplitIncomeUseCase(
          repository,
          externalTransactionRepository,
          transactionManager,
          domainService,
          dispatch,
        );
      },
      deps: [INCOME_REPOSITORY, EXTERNAL_TRANSACTION_REPOSITORY, TRANSACTION_MANAGER, IncomeDomainService],
    },
    {
      provide: ExportIncomesUseCase,
      useFactory: (repository: IIncomeRepository) => new ExportIncomesUseCase(repository),
      deps: [INCOME_REPOSITORY],
    },
    {
      provide: BulkSetIncomeCategoryUseCase,
      useFactory: (repository: IIncomeRepository) => {
        const dispatch = injectDispatch(TransactionEvents);
        return new BulkSetIncomeCategoryUseCase(repository, dispatch);
      },
      deps: [INCOME_REPOSITORY],
    },
  ];
}
